'use client'

import Navbar from '@/components/Navbar'
import Footer from '@/components/Footer'
import { motion } from 'framer-motion'
import Link from 'next/link'
import { notFound } from 'next/navigation'
import { useParams } from 'next/navigation'
import { useState, useEffect } from 'react'

// 应用案例数据
const applicationCases = {
  satellite: {
    id: 'satellite',
    name: '卫星',
    category: '航空航天',
    description: '为卫星光学系统提供极致的光吸收解决方案，确保高精度观测和数据采集',
    longDescription: `
      在卫星光学系统中，杂散光的抑制是确保成像质量的关键因素。画玺太阳黑洞6.0超黑材料凭借其>99.8%的吸收率，
      为卫星光学系统提供了革命性的解决方案。我们的材料能够有效吸收各个角度的入射光，显著降低系统噪声，
      提升图像对比度和信噪比。
    `,
    features: [
      {
        title: '超高吸收率',
        description: '吸收率>99.8%，有效抑制杂散光，提升成像质量',
        icon: '🎯'
      },
      {
        title: '耐高温性能',
        description: '可在-100°C至+200°C温度范围内稳定工作',
        icon: '🌡️'
      },
      {
        title: '耐辐射特性',
        description: '抗宇宙射线辐射，长期性能稳定',
        icon: '☢️'
      },
      {
        title: '轻量化设计',
        description: '密度低，不增加卫星载荷重量',
        icon: '🪶'
      }
    ],
    applications: [
      '光学遥感器',
      '星敏感器', 
      '地球观测相机',
      '空间望远镜',
      '红外成像仪',
      '激光通信设备'
    ],
    technicalSpecs: [
      { parameter: '吸收率', value: '>99.8%', range: '250nm-2500nm' },
      { parameter: '工作温度', value: '-100°C ~ +200°C', range: '全光谱范围' },
      { parameter: '厚度', value: '10-100μm', range: '可定制' },
      { parameter: '附着力', value: '5B级', range: 'ASTM D3359' }
    ],
    image: 'https://images.unsplash.com/photo-1446776877081-d282a0f896e2?w=1200&h=800&fit=crop',
    icon: '🛰️'
  },
  missile: {
    id: 'missile',
    name: '导弹',
    category: '国防军工',
    description: '为导弹制导系统提供完美的光吸收材料，确保制导精度和隐蔽性',
    longDescription: `
      现代导弹制导系统对光学性能要求极高，任何杂散光都可能影响制导精度。
      画玺太阳黑洞6.0超黑材料为导弹光学系统提供了完美的解决方案，
      确保系统在各种复杂环境下都能保持优异的性能表现。
    `,
    features: [
      {
        title: '极低反射率',
        description: '反射率<0.2%，大幅提升导弹隐蔽性',
        icon: '🛡️'
      },
      {
        title: '抗冲击性能',
        description: '抗冲击、抗振动，适应高速飞行环境',
        icon: '💥'
      },
      {
        title: '快速响应',
        description: '光学性能稳定，响应速度快',
        icon: '⚡'
      },
      {
        title: '环境适应',
        description: '适应各种恶劣环境条件',
        icon: '🌍'
      }
    ],
    applications: [
      '光学制导系统',
      '红外导引头',
      '激光测距仪',
      '目标识别系统',
      '光学引信',
      '夜视设备'
    ],
    technicalSpecs: [
      { parameter: '反射率', value: '<0.2%', range: '全光谱范围' },
      { parameter: '抗冲击', value: '50G', range: 'MIL-STD-810' },
      { parameter: '工作温度', value: '-55°C ~ +125°C', range: '军用标准' },
      { parameter: '可靠性', value: '99.9%', range: 'MTBF>10000h' }
    ],
    image: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=1200&h=800&fit=crop',
    icon: '🚀'
  },
  camera: {
    id: 'camera',
    name: '汽车摄像头',
    category: '汽车电子',
    description: '为汽车摄像头提供完美的杂散光抑制解决方案，确保清晰成像',
    longDescription: `
      汽车摄像头在复杂的光照环境下工作，需要有效抑制杂散光以确保成像质量。
      画玺太阳黑洞6.0超黑材料为汽车摄像头提供了完美的解决方案，
      确保在各种天气和光照条件下都能获得清晰的图像。
    `,
    features: [
      {
        title: '超低反射',
        description: '反射率<0.2%，有效抑制杂散光',
        icon: '📷'
      },
      {
        title: '耐候性能',
        description: '耐紫外线、耐臭氧、耐高低温',
        icon: '🌤️'
      },
      {
        title: '稳定性能',
        description: '长期性能稳定，不老化',
        icon: '🔧'
      },
      {
        title: '易加工性',
        description: '易于加工成型，适合批量生产',
        icon: '⚙️'
      }
    ],
    applications: [
      '前置摄像头',
      '后置摄像头',
      '环视摄像头',
      '行车记录仪',
      '夜视摄像头',
      '盲区监测摄像头'
    ],
    technicalSpecs: [
      { parameter: '反射率', value: '<0.2%', range: '可见光范围' },
      { parameter: '工作温度', value: '-40°C ~ +85°C', range: '汽车级' },
      { parameter: '湿度范围', value: '0-95%RH', range: '无冷凝' },
      { parameter: '使用寿命', value: '>10年', range: '正常使用条件' }
    ],
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200&h=800&fit=crop',
    icon: '📹'
  },
  cameraBracket: {
    id: 'camera-bracket',
    name: '汽车摄像头支架',
    category: '汽车配件',
    description: '为汽车摄像头支架提供完美的黑化解决方案，消除反射干扰',
    longDescription: `
      汽车摄像头支架的光学性能直接影响摄像头的成像质量。
      画玺太阳黑洞6.0超黑材料为摄像头支架提供了完美的黑化解决方案，
      有效消除支架表面的反射光，确保摄像头获得纯净的图像信号。
    `,
    features: [
      {
        title: '完美黑化',
        description: '吸收率>99.5%，消除支架反射',
        icon: '⚫'
      },
      {
        title: '耐腐蚀性',
        description: '优异的耐腐蚀性能，适应汽车环境',
        icon: '🛡️'
      },
      {
        title: '附着力强',
        description: '与金属基材附着力优异',
        icon: '🔗'
      },
      {
        title: '成本优势',
        description: '工艺简单，成本可控',
        icon: '💰'
      }
    ],
    applications: [
      '摄像头支架',
      '安装底座',
      '调节机构',
      '防护外壳',
      '连接件',
      '固定夹具'
    ],
    technicalSpecs: [
      { parameter: '吸收率', value: '>99.5%', range: '全光谱范围' },
      { parameter: '附着力', value: '5B级', range: 'ASTM D3359' },
      { parameter: '耐腐蚀', value: '1000h', range: '盐雾试验' },
      { parameter: '硬度', value: '2H', range: '铅笔硬度' }
    ],
    image: 'https://images.unsplash.com/photo-1583121274602-3e2820c69888?w=1200&h=800&fit=crop',
    icon: '🔧'
  },
  cameraPcba: {
    id: 'camera-pcba',
    name: '汽车摄像头PCBA板',
    category: '汽车电子',
    description: '为汽车摄像头PCBA板提供完美的黑化保护，提升可靠性和性能',
    longDescription: `
      汽车摄像头PCBA板需要在复杂的环境中稳定工作，光学性能要求极高。
      画玺太阳黑洞6.0超黑材料为PCBA板提供了完美的黑化保护，
      有效吸收杂散光，提升电路板的可靠性和整体性能。
    `,
    features: [
      {
        title: '电路保护',
        description: '保护电路免受光学干扰',
        icon: '🔌'
      },
      {
        title: '散热性能',
        description: '良好的热辐射性能，有助散热',
        icon: '🔥'
      },
      {
        title: '绝缘性能',
        description: '优异的电绝缘性能',
        icon: '⚡'
      },
      {
        title: '工艺兼容',
        description: '与SMT工艺完全兼容',
        icon: '🏭'
      }
    ],
    applications: [
      'PCBA板黑化',
      '芯片封装',
      '连接器黑化',
      '散热器黑化',
      '屏蔽罩',
      '光学窗口'
    ],
    technicalSpecs: [
      { parameter: '吸收率', value: '>99.5%', range: '全光谱范围' },
      { parameter: '绝缘电阻', value: '>10^12Ω', range: '室温条件' },
      { parameter: '导热系数', value: '>0.1W/m·K', range: '室温条件' },
      { parameter: '耐焊性', value: '260°C', range: '10s' }
    ],
    image: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=1200&h=800&fit=crop',
    icon: '🔌'
  },
  lidar: {
    id: 'lidar',
    name: '激光雷达',
    category: '自动驾驶',
    description: '为激光雷达系统提供极致的光学性能，确保精确的测距和成像',
    longDescription: `
      激光雷达系统对光学性能要求极高，需要有效抑制杂散光和反射。
      画玺太阳黑洞6.0超黑材料为激光雷达提供了完美的解决方案，
      确保系统在各种环境条件下都能获得精确的测距和成像数据。
    `,
    features: [
      {
        title: '激光吸收',
        description: '对激光波长超高吸收率',
        icon: '💫'
      },
      {
        title: '抗干扰性',
        description: '有效抑制环境光干扰',
        icon: '🌟'
      },
      {
        title: '精密制造',
        description: '适合精密光学元件制造',
        icon: '🔬'
      },
      {
        title: '长期稳定',
        description: '光学性能长期稳定不变',
        icon: '📊'
      }
    ],
    applications: [
      '激光发射器',
      '接收器光学',
      '扫描机构',
      '光学窗口',
      '反射镜',
      '滤光片'
    ],
    technicalSpecs: [
      { parameter: '激光吸收率', value: '>99.8%', range: '905nm/1550nm' },
      { parameter: '角度特性', value: '全角度', range: '0-90°入射角' },
      { parameter: '温度稳定性', value: '<0.1%', range: '-40°C~85°C' },
      { parameter: '损伤阈值', value: '>10J/cm²', range: '纳秒激光' }
    ],
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200&h=800&fit=crop',
    icon: '🎯'
  },
  opticalInstrument: {
    id: 'optical-instrument',
    name: '光学仪器',
    category: '精密仪器',
    description: '为各类光学仪器提供完美的黑化解决方案，提升测量精度和可靠性',
    longDescription: `
      光学仪器的性能很大程度上取决于其光学系统的杂散光控制能力。
      画玺太阳黑洞6.0超黑材料为各类光学仪器提供了完美的黑化解决方案，
      有效提升仪器的测量精度和可靠性，确保获得准确的测量结果。
    `,
    features: [
      {
        title: '超高精度',
        description: '满足精密仪器的高精度要求',
        icon: '🎯'
      },
      {
        title: '多功能性',
        description: '适用于各种光学仪器',
        icon: '🔧'
      },
      {
        title: '重复性好',
        description: '工艺重复性优异',
        icon: '🔄'
      },
      {
        title: '一致性强',
        description: '批次间性能一致性好',
        icon: '✅'
      }
    ],
    applications: [
      '显微镜',
      '望远镜',
      '分光光度计',
      '椭偏仪',
      '干涉仪',
      '光学平台'
    ],
    technicalSpecs: [
      { parameter: '吸收率', value: '>99.8%', range: '全光谱范围' },
      { parameter: '均匀性', value: '<±1%', range: '全表面' },
      { parameter: '稳定性', value: '<0.1%', range: '长期稳定性' },
      { parameter: '清洁度', value: 'Class 100', range: '洁净室标准' }
    ],
    image: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=1200&h=800&fit=crop',
    icon: '🔭'
  },
  spectrometer: {
    id: 'spectrometer',
    name: '光谱仪',
    category: '分析仪器',
    description: '为光谱仪提供完美的光学性能，确保精确的光谱分析',
    longDescription: `
      光谱仪对光学系统的性能要求极高，需要有效抑制杂散光以确保光谱分析的准确性。
      画玺太阳黑洞6.0超黑材料为光谱仪提供了完美的解决方案，
      确保在各种分析条件下都能获得精确的光谱数据。
    `,
    features: [
      {
        title: '光谱平坦',
        description: '在整个光谱范围内性能一致',
        icon: '🌈'
      },
      {
        title: '低杂散光',
        description: '极低的杂散光水平',
        icon: '✨'
      },
      {
        title: '高信噪比',
        description: '显著提升光谱信噪比',
        icon: '📈'
      },
      {
        title: '波长稳定',
        description: '波长响应稳定性好',
        icon: '📊'
      }
    ],
    applications: [
      '紫外光谱仪',
      '可见光谱仪',
      '红外光谱仪',
      '荧光光谱仪',
      '拉曼光谱仪',
      '原子吸收光谱仪'
    ],
    technicalSpecs: [
      { parameter: '光谱范围', value: '200-2500nm', range: '全光谱覆盖' },
      { parameter: '杂散光', value: '<0.01%', range: '典型值' },
      { parameter: '信噪比', value: '>1000:1', range: '改善效果' },
      { parameter: '波长精度', value: '<±0.1nm', range: '系统精度' }
    ],
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200&h=800&fit=crop',
    icon: '🌈'
  },
  toy: {
    id: 'toy',
    name: '儿童玩具（玩具涂黑）',
    category: '消费品',
    description: '为高端儿童玩具提供独特的黑色美学和功能性解决方案',
    longDescription: `
      高端儿童玩具不仅要求安全性和耐用性，也越来越注重美学设计。
      画玺太阳黑洞6.0超黑材料为儿童玩具提供了独特的黑色美学解决方案，
      同时具备良好的功能性和安全性，满足高端玩具的严格要求。
    `,
    features: [
      {
        title: '安全环保',
        description: '符合玩具安全标准，无毒无害',
        icon: '🛡️'
      },
      {
        title: '独特美学',
        description: '极致黑色，独特的视觉体验',
        icon: '🎨'
      },
      {
        title: '耐磨损',
        description: '优异的耐磨性能',
        icon: '💪'
      },
      {
        title: '易清洁',
        description: '表面光滑，易于清洁维护',
        icon: '🧽'
      }
    ],
    applications: [
      '高端玩具',
      '收藏品',
      '模型玩具',
      '益智玩具',
      '装饰玩具',
      '主题玩具'
    ],
    technicalSpecs: [
      { parameter: '安全等级', value: 'EN71/ASTM', range: '玩具安全标准' },
      { parameter: '耐磨性', value: '>1000次', range: '摩擦测试' },
      { parameter: '耐水性', value: 'IPX4', range: '防水等级' },
      { parameter: '环保性', value: 'RoHS', range: '环保标准' }
    ],
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200&h=800&fit=crop',
    icon: '🧸'
  },
  homeDecoration: {
    id: 'home-decoration',
    name: '家庭装修（黑色爱好者）',
    category: '建筑装饰',
    description: '为黑色爱好者提供极致的黑色装饰解决方案，创造独特的空间美学',
    longDescription: `
      黑色代表着神秘、优雅和现代感，越来越多的消费者偏爱黑色装饰。
      画玺太阳黑洞6.0超黑材料为家庭装修提供了极致的黑色解决方案，
      为黑色爱好者创造独特的空间美学和视觉体验。
    `,
    features: [
      {
        title: '极致黑色',
        description: '世界上最黑的材料之一',
        icon: '⚫'
      },
      {
        title: '装饰效果',
        description: '独特的装饰效果和视觉冲击力',
        icon: '🏠'
      },
      {
        title: '耐候性',
        description: '优异的室内外耐候性能',
        icon: '🌤️'
      },
      {
        title: '定制化',
        description: '可根据需求定制颜色和效果',
        icon: '🎨'
      }
    ],
    applications: [
      '墙面装饰',
      '家具表面',
      '装饰品',
      '灯具配件',
      '门窗框架',
      '艺术装置'
    ],
    technicalSpecs: [
      { parameter: '颜色深度', value: 'L*<5', range: 'CIE Lab色空间' },
      { parameter: '耐候性', value: '>1000h', range: 'UV老化测试' },
      { parameter: '附着力', value: '1级', range: '划格法测试' },
      { parameter: '环保性', value: 'A+', range: '法国VOC标准' }
    ],
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200&h=800&fit=crop',
    icon: '🏠'
  }
}

export default function ApplicationDetailPage() {
  const params = useParams()
  const id = params.id as string
  const [caseData, setCaseData] = useState<any>(null)
  const [categoryContent, setCategoryContent] = useState<string>('')
  const [loading, setLoading] = useState(true)

  // 获取后台栏目数据并匹配应用案例
  useEffect(() => {
    const fetchApplicationData = async () => {
      try {
        setLoading(true)
        // 获取所有栏目
        const response = await fetch('/api/categories')
        const result = await response.json()
        
        if (result.success && result.data) {
          // 查找匹配的栏目（通过slug或name）
          const matchedCategory = result.data.find((cat: any) => 
            (cat.slug === id || cat.name === id) && cat.type === 'page'
          )
          
          if (matchedCategory) {
            // 查找对应的静态数据
            const staticCase = applicationCases[matchedCategory.slug as keyof typeof applicationCases] || 
                              Object.values(applicationCases).find((item: any) => item.name === matchedCategory.name)
            
            // 合并数据，优先使用后台数据
            const mergedData = {
              ...staticCase,
              id: matchedCategory.slug || matchedCategory.id.toString(),
              name: matchedCategory.name,
              category: matchedCategory.description || '应用案例',
              description: matchedCategory.listDisplayText || matchedCategory.description || '应用案例详情',
              longDescription: matchedCategory.pageContent || staticCase?.longDescription || '应用案例详细描述',
              // 背景图只能使用栏目背景图
              backgroundImage: matchedCategory.backgroundImage
            }
            
            setCaseData(mergedData)
            
            // 设置单页内容
            if (matchedCategory.pageContent) {
              setCategoryContent(matchedCategory.pageContent)
            }
          } else {
            // 如果没有匹配的栏目，使用静态数据
            const staticCase = applicationCases[id as keyof typeof applicationCases]
            if (staticCase) {
              setCaseData(staticCase)
            } else {
              notFound()
            }
          }
        }
      } catch (error) {
        console.error('获取应用数据失败:', error)
        // 出错时使用静态数据
        const staticCase = applicationCases[id as keyof typeof applicationCases]
        if (staticCase) {
          setCaseData(staticCase)
        } else {
          notFound()
        }
      } finally {
        setLoading(false)
      }
    }

    fetchApplicationData()
  }, [id])

  // 显示加载状态
  if (loading || !caseData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900">
        <Navbar />
        <div className="container mx-auto px-4 py-16">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  // 根据是否有动态内容决定显示内容
  const displayContent = categoryContent 
    ? categoryContent 
    : caseData.longDescription

  return (
    <>
      {/* 导航栏 */}
      <Navbar />
      
      {/* 页面头部 */}
      <section className="relative py-32 overflow-hidden">
        {/* 背景图 - 调用后台栏目背景图 */}
        <div className="absolute inset-0 z-0">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `url('${caseData.backgroundImage}')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              filter: 'brightness(0.8) contrast(1.2)'
            }}
          >
            {/* 渐变遮罩层 */}
            <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/40 to-transparent"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
          </div>
        </div>
        
        {/* 动态背景装饰 */}
        <div className="absolute inset-0 opacity-20 z-10">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-gradient-to-r from-blue-500/30 to-purple-600/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute top-1/2 right-1/4 w-48 h-48 bg-gradient-to-r from-orange-500/30 to-red-600/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute bottom-1/4 left-1/3 w-56 h-56 bg-gradient-to-r from-cyan-500/30 to-blue-600/30 rounded-full blur-3xl animate-pulse delay-2000"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 text-center z-20">
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-7xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent mb-6"
          >
            {caseData.name}
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-xl md:text-2xl text-gray-300 leading-relaxed mb-12"
          >
            {caseData.description}
          </motion.p>
          
          {/* 装饰性元素 - 来自新闻中心 */}
          <div className="flex justify-center items-center space-x-4 text-blue-300/80">
            <div className="w-12 h-0.5 bg-gradient-to-r from-transparent via-blue-400 to-transparent"></div>
            <span className="text-sm font-medium drop-shadow-md">●</span>
            <div className="w-12 h-0.5 bg-gradient-to-r from-transparent via-purple-400 to-transparent"></div>
          </div>
        </div>
        
        {/* 底部渐变过渡 */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent z-20"></div>
      </section>

      {/* 应用详情展示 - 与新闻详情保持一致 */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="prose prose-invert max-w-none">
            <div className="relative bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 md:p-12 border border-gray-700/50 shadow-2xl shadow-blue-500/10">
              {/* 装饰边框 */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-500/10 via-purple-500/5 to-transparent pointer-events-none"></div>
              <div className="absolute inset-0 rounded-2xl border border-white/5 pointer-events-none"></div>
              
              <div 
                className="news-content text-gray-300 relative z-10"
                dangerouslySetInnerHTML={{ __html: displayContent }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* 页脚 */}
      <Footer />
    </>
  )
}